import { useNavigate } from 'react-router-dom';
import { StepNavigation } from '../../campaign/components/steps/StepNavigation';

import { useEffect } from 'react';
import { ProductType } from '~/models/product/ProductType';
import { startCustomerVerification } from '../../campaign/verification/vipps/api';
import { useBasket } from '../useBasket';
import { StepType, v2Routes, v2Steps } from '../v2routes';

export const StepNavigationV2: React.FC<{ isSummaryOpen: boolean; currentStep: number }> = ({ isSummaryOpen, currentStep }) => {
  const navigate = useNavigate();
  const setStepError = useBasket((s) => s.setStepError);
  const setPostVerificationRedirect = useBasket((s) => s.setPostVerificationRedirect);

  // ensure all previous step conditions are met
  useEffect(() => {
    const currentStepData = v2Steps[currentStep];
    // 1. ensure previous steps are valid, otherwise redirect there
    for (const step of v2Steps.slice(0, currentStep)) {
      if (!isStepValid(step.type, currentStepData.type)) {
        console.warn(`Previous step for ${currentStepData.title} is invalid (${step.title}). Redirecting to ${step.stepPath}`);
        navigate(step.stepPath, { replace: true });
        break;
      }
    }
  }, [currentStep, navigate]);

  const onNext = () => {
    const stepType = v2Steps[currentStep].type;
    if (!isStepValid(stepType)) {
      setStepError(stepErrorMessages[stepType]);
      return;
    }
    setStepError(undefined);
    // On contact info step we first need to perform verification
    if (stepType === 'contactInfo') {
      const basketState = useBasket.getState();
      // Missing verification data, so we redirect to provider
      if (!basketState.verificationResponse) {
        setPostVerificationRedirect(location.pathname);
        startCustomerVerification(basketState.selectedVerificationProvider).then((res) => {
          window.location.href = res;
        });
        return;
      }
      // Validate contact form
      const contactForm = document.querySelector('form[name="contactInfo"]') as HTMLFormElement | null;
      if (!contactForm) return;
      // If valid, call submit to store data in basket store
      if (contactForm.reportValidity() !== true) {
        contactForm.setAttribute('data-submitted', 'true');
        return;
      }
      // Validate delayed activation
      if (basketState.useDelayedActivation && basketState.delayedActivationDate == null) {
        const datePicker = document.querySelector('input#delayed-activation-date');
        if (datePicker instanceof HTMLInputElement) {
          datePicker.setCustomValidity('Du må velge en dato for utsatt aktivering');
          datePicker.reportValidity();
        }
        return;
      }

      // Submit form so that we store contact info to basket
      contactForm.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
    navigate(v2Steps[currentStep + 1].stepPath);
  };

  return (
    <StepNavigation
      isSummaryOpen={isSummaryOpen}
      isLastStep={currentStep === v2Steps.length - 1}
      onNext={onNext}
      onPrev={() => {
        const stepPath = v2Steps[currentStep - 1]?.stepPath ?? v2Routes.index;
        navigate(stepPath);
      }}
    />
  );
};

const isStepValid = (type: StepType, currentStepType?: StepType): boolean => {
  switch (type) {
    case 'premium':
    case 'summary': {
      return true;
    }
    case 'equipment': {
      // must select a receiver
      return useBasket.getState().selectedProducts.some((sp) => sp.productType === ProductType.Receiver);
    }
    case 'internet':
      return Boolean(useBasket.getState().selectedISP);
    case 'contactInfo': {
      // 1. check has verification provider and response
      if (useBasket.getState().selectedVerificationProvider === '') {
        return false;
      }
      // special case for summary to ensure we have valid customer data
      if (currentStepType === 'summary') {
        const state = useBasket.getState();
        return state.verificationResponse != null && state.contactInfo != null;
      }
      // 2. contact form validity checked in onNext
      return true;
    }
  }
};

const stepErrorMessages: Record<StepType, string> = {
  premium: '',
  equipment: 'Du må velge minst én TV-boks.',
  internet: 'Du må velge et av alternativene.',
  contactInfo: 'Du må velge en verifiseringsmetode.',
  summary: '',
};
